import { useEffect, useState } from 'react';

export interface DailyQuestClaimToastProps {
  /** Reward to display. {@code null} clears the toast. */
  reward: { coins: number; gems: number } | null;
  /** Whether the claim was successful or on-cooldown. Failure path
   * shows a "come back tomorrow" message in muted teal instead. */
  success: boolean;
  /** Screen position to anchor the toast to (the tapped node's
   * centre). Toast floats UP and fades from this point. */
  anchorX: number;
  anchorY: number;
  /** Called once the toast finishes its fade-out. The host clears its
   * state on this so a re-claim re-mounts cleanly. */
  onDone: () => void;
}

/**
 * Lightweight floating-label toast for the world-map Daily Quest
 * inline-claim. Mounts at the node's screen position, drifts up ~80px
 * over 1.6s while fading from 0 → 1 → 0 alpha. Replaces the previous
 * full-scene-switch flow where the player was sent into a separate
 * "Daily Quest" canvas just to tap a single CLAIM button.
 *
 * <p>Producer audit Phase 2.10. Particle-flight to the HUD counter is
 * deferred — the existing balance/gem counters in the HUD already
 * react to {@code profile.events.on('balance-changed')} so the
 * numbers update synchronously when {@code claimDailyQuest} fires.
 * The toast is the visible "you got something" cue.
 */
export function DailyQuestClaimToast({
  reward,
  success,
  anchorX,
  anchorY,
  onDone,
}: DailyQuestClaimToastProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!reward && success) return;
    setVisible(true);
    const timer = window.setTimeout(() => {
      setVisible(false);
      onDone();
    }, 1600);
    return () => window.clearTimeout(timer);
  }, [reward, success, onDone]);

  if (!visible) return null;

  const label = success
    ? `+${reward?.coins ?? 0} coins · +${reward?.gems ?? 0} gem`
    : 'Already claimed today. Back tomorrow';

  return (
    <>
      <style>{`
        @keyframes dailyQuestRise {
          0%   { opacity: 0; transform: translate(-50%, 0); }
          15%  { opacity: 1; transform: translate(-50%, -10px); }
          80%  { opacity: 1; transform: translate(-50%, -64px); }
          100% { opacity: 0; transform: translate(-50%, -84px); }
        }
      `}</style>
      <div
        style={{
          position: 'fixed',
          left: anchorX,
          top: anchorY,
          zIndex: 100,
          padding: '8px 16px',
          borderRadius: 999,
          background: success
            ? 'linear-gradient(180deg, #FFE48A 0%, #FFC940 100%)'
            : 'linear-gradient(180deg, rgba(98,128,154,0.9) 0%, rgba(60,80,110,0.9) 100%)',
          color: success ? '#3A1F00' : '#E9F0FF',
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: 0.5,
          textShadow: success ? 'none' : '0 1px 2px rgba(0,0,0,0.4)',
          boxShadow:
            '0 0 0 2px rgba(255,255,255,0.4), 0 6px 18px rgba(0,0,0,0.45)',
          pointerEvents: 'none',
          animation: 'dailyQuestRise 1.6s ease-out forwards',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </div>
    </>
  );
}
